import React, { useState } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminDataExplorerQuery,
  useLazyGetUserSimulationRunsQuery,
  useLazyGetSimulationRunLogsQuery,
  useLazyGetUserMicrolearningDataQuery,
  UserSimulationRun,
  SimulationRunLogsData,
  UserMicrolearningJournalEntry,
  UserMicrolearningScenarioCompletion,
  UserMicrolearningWeeklyResponse,
} from "../../app/services/meseeknaApiV2"
import { FiSearch, FiUser, FiChevronDown, FiChevronUp, FiX, FiDatabase, FiBook, FiCheckCircle, FiCalendar } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUser {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronUpIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronUp {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const XIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const DatabaseIcon = (props: { size?: number; className?: string }): JSX.Element => <FiDatabase {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BookIcon = (props: { size?: number; className?: string }): JSX.Element => <FiBook {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckCircleIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CalendarIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCalendar {...props} />

type MicrolearningTab = "journal" | "scenarios" | "weekly"

const RawDataPage: React.FC = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")

  // Selected user state
  const [selectedUser, setSelectedUser] = useState<{
    id: number
    email: string
    firstName: string | null
    lastName: string | null
    organizationName: string | null
  } | null>(null)

  // Simulation runs state
  const [simulationRuns, setSimulationRuns] = useState<UserSimulationRun[]>([])
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
  const [runLogs, setRunLogs] = useState<SimulationRunLogsData | null>(null)
  const [loadingRunLogs, setLoadingRunLogs] = useState(false)

  // Microlearning data state
  const [microlearningTab, setMicrolearningTab] = useState<MicrolearningTab>("journal")
  const [journalEntries, setJournalEntries] = useState<UserMicrolearningJournalEntry[]>([])
  const [scenarioCompletions, setScenarioCompletions] = useState<UserMicrolearningScenarioCompletion[]>([])
  const [weeklyResponses, setWeeklyResponses] = useState<UserMicrolearningWeeklyResponse[]>([])
  const [microlearningStats, setMicrolearningStats] = useState<{
    totalJournalEntries: number
    totalScenariosCompleted: number
    scenarioAccuracy: number
    totalWeeklyResponses: number
  } | null>(null)

  // Loading states
  const [loadingSimulationRuns, setLoadingSimulationRuns] = useState(false)
  const [loadingMicrolearning, setLoadingMicrolearning] = useState(false)

  // API hooks
  const [fetchSimulationRuns] = useLazyGetUserSimulationRunsQuery()
  const [fetchRunLogs] = useLazyGetSimulationRunLogsQuery()
  const [fetchMicrolearningData] = useLazyGetUserMicrolearningDataQuery()

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Search query
  const { data: searchResults, isLoading: isSearching, isFetching } = useGetAdminDataExplorerQuery(
    { q: debouncedQuery, type: "users" },
    { skip: debouncedQuery.length < 2 }
  )

  const users = searchResults?.data?.users || []

  // Handle user selection
  const handleSelectUser = async (user: {
    id: number
    email: string
    firstName: string | null
    lastName: string | null
    organizationName: string | null
  }) => {
    setSelectedUser(user)
    setSearchQuery("")
    setDebouncedQuery("")
    setExpandedRunId(null)
    setRunLogs(null)

    // Fetch simulation runs
    setLoadingSimulationRuns(true)
    try {
      const runsResult = await fetchSimulationRuns(user.id).unwrap()
      setSimulationRuns(runsResult.data.runs || [])
    } catch (error) {
      console.error("Failed to fetch simulation runs:", error)
      setSimulationRuns([])
    } finally {
      setLoadingSimulationRuns(false)
    }

    // Fetch microlearning data
    setLoadingMicrolearning(true)
    try {
      const mlResult = await fetchMicrolearningData(user.id).unwrap()
      setJournalEntries(mlResult.data.journalEntries || [])
      setScenarioCompletions(mlResult.data.scenarioCompletions || [])
      setWeeklyResponses(mlResult.data.weeklyResponses || [])
      setMicrolearningStats(mlResult.data.stats)
    } catch (error) {
      console.error("Failed to fetch microlearning data:", error)
      setJournalEntries([])
      setScenarioCompletions([])
      setWeeklyResponses([])
      setMicrolearningStats(null)
    } finally {
      setLoadingMicrolearning(false)
    }
  }

  // Handle run expansion to load logs
  const handleExpandRun = async (runId: number) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null)
      setRunLogs(null)
      return
    }

    setExpandedRunId(runId)
    setLoadingRunLogs(true)
    try {
      const logsResult = await fetchRunLogs(runId).unwrap()
      setRunLogs(logsResult.data)
    } catch (error) {
      console.error("Failed to fetch run logs:", error)
      setRunLogs(null)
    } finally {
      setLoadingRunLogs(false)
    }
  }

  // Clear selection
  const clearSelection = () => {
    setSelectedUser(null)
    setSimulationRuns([])
    setExpandedRunId(null)
    setRunLogs(null)
    setJournalEntries([])
    setScenarioCompletions([])
    setWeeklyResponses([])
    setMicrolearningStats(null)
  }

  // Format date helper
  const formatDate = (date: string | null) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDateShort = (date: string | null) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DatabaseIcon size={24} className="text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">View Raw Data</h1>
                  <p className="text-gray-500">
                    Search for a user to view their simulation run logs and microlearning responses
                  </p>
                </div>
              </div>
            </div>

            {/* Search Box */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="relative">
                <SearchIcon
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by name or email (min 2 characters)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                {isFetching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Search Results Dropdown */}
              {debouncedQuery.length >= 2 && !selectedUser && (
                <div className="mt-2 border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 text-center text-gray-500">Searching...</div>
                  ) : users.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No users found</div>
                  ) : (
                    users.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <UserIcon size={20} className="text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.firstName || user.lastName
                                ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                                : "No Name"}
                            </p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          {user.organizationName && (
                            <span className="ml-auto flex items-center gap-1 text-sm text-gray-500 px-3 py-1 bg-gray-100 rounded-full">
                              <BuildingIcon size={14} />
                              {user.organizationName}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected User Display */}
            {selectedUser && (
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                      <UserIcon size={24} className="text-purple-700" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg text-gray-900">
                        {selectedUser.firstName || selectedUser.lastName
                          ? `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()
                          : "No Name"}
                      </h2>
                      <p className="text-sm text-gray-600">{selectedUser.email}</p>
                      {selectedUser.organizationName && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          <BuildingIcon size={12} className="inline mr-1" />
                          {selectedUser.organizationName}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={clearSelection}
                    className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <XIcon size={20} className="text-gray-500" />
                  </button>
                </div>
              </div>
            )}

            {/* Data Sections - only show when user is selected */}
            {selectedUser && (
              <div className="space-y-6">
                {/* Simulation Runs Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                      <DatabaseIcon size={18} className="text-blue-600" />
                      Simulation Runs ({simulationRuns.length})
                    </h2>
                  </div>

                  {loadingSimulationRuns ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-gray-500">Loading simulation runs...</p>
                    </div>
                  ) : simulationRuns.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No simulation runs found for this user
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {simulationRuns.map((run) => (
                        <div key={run.runId}>
                          <div
                            onClick={() => run.hasRunLogs && handleExpandRun(run.runId)}
                            className={`p-4 ${run.hasRunLogs ? "hover:bg-gray-50 cursor-pointer" : ""} transition-colors`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{run.simulationName}</p>
                                <p className="text-sm text-gray-500">
                                  Run ID: {run.runId} | Test ID: {run.testId}
                                </p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right text-sm">
                                  <p className="text-gray-600">Assigned: {formatDateShort(run.dateAssigned)}</p>
                                  {run.dateCompleted && (
                                    <p className="text-green-600">Completed: {formatDateShort(run.dateCompleted)}</p>
                                  )}
                                </div>
                                {run.hasRunLogs ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                      {run.runLogCount} logs
                                    </span>
                                    {expandedRunId === run.runId ? (
                                      <ChevronUpIcon size={20} className="text-gray-400" />
                                    ) : (
                                      <ChevronDownIcon size={20} className="text-gray-400" />
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400 bg-gray-50 px-2 py-1 rounded">
                                    No logs
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Run Logs */}
                          {expandedRunId === run.runId && (
                            <div className="bg-blue-50 p-4 border-t border-blue-100">
                              {loadingRunLogs ? (
                                <div className="text-center py-4">
                                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                  <p className="text-sm text-gray-500">Loading run logs...</p>
                                </div>
                              ) : runLogs ? (
                                <div>
                                  <div className="flex items-center gap-4 mb-4">
                                    <h4 className="font-medium text-gray-900">
                                      Run Logs ({runLogs.totalEntries} entries)
                                    </h4>
                                    <div className="flex gap-2 flex-wrap">
                                      {Object.entries(runLogs.entriesByType).map(([type, count]) => (
                                        <span
                                          key={type}
                                          className="text-xs bg-white px-2 py-1 rounded border border-gray-200"
                                        >
                                          {type}: {count}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="bg-white rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                          <th className="text-left p-2 font-medium text-gray-600">Time</th>
                                          <th className="text-left p-2 font-medium text-gray-600">Type</th>
                                          <th className="text-left p-2 font-medium text-gray-600">Details</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {runLogs.entries.map((entry, index) => (
                                          <tr key={index} className="hover:bg-gray-50">
                                            <td className="p-2 text-gray-500 whitespace-nowrap">
                                              {entry.elapsedTime}
                                            </td>
                                            <td className="p-2">
                                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                                {entry.type}
                                              </span>
                                            </td>
                                            <td className="p-2 text-gray-700">
                                              {entry.responseText || entry.B4 || entry.B5 || "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-center text-gray-500 py-4">Failed to load run logs</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Microlearning Data Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                        <BookIcon size={18} className="text-green-600" />
                        Microlearning Data
                      </h2>
                      {microlearningStats && (
                        <div className="flex gap-4 text-sm">
                          <span className="text-gray-600">
                            Journal: <span className="font-medium">{microlearningStats.totalJournalEntries}</span>
                          </span>
                          <span className="text-gray-600">
                            Scenarios: <span className="font-medium">{microlearningStats.totalScenariosCompleted}</span>
                            {microlearningStats.scenarioAccuracy > 0 && (
                              <span className="text-green-600 ml-1">
                                ({microlearningStats.scenarioAccuracy.toFixed(0)}% correct)
                              </span>
                            )}
                          </span>
                          <span className="text-gray-600">
                            Weekly: <span className="font-medium">{microlearningStats.totalWeeklyResponses}</span>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setMicrolearningTab("journal")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          microlearningTab === "journal"
                            ? "bg-green-100 text-green-700 font-medium"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <BookIcon size={16} />
                        Journal ({journalEntries.length})
                      </button>
                      <button
                        onClick={() => setMicrolearningTab("scenarios")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          microlearningTab === "scenarios"
                            ? "bg-green-100 text-green-700 font-medium"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <CheckCircleIcon size={16} />
                        Scenarios ({scenarioCompletions.length})
                      </button>
                      <button
                        onClick={() => setMicrolearningTab("weekly")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          microlearningTab === "weekly"
                            ? "bg-green-100 text-green-700 font-medium"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        <CalendarIcon size={16} />
                        Weekly ({weeklyResponses.length})
                      </button>
                    </div>
                  </div>

                  {loadingMicrolearning ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-gray-500">Loading microlearning data...</p>
                    </div>
                  ) : (
                    <div className="p-4">
                      {/* Journal Entries Tab */}
                      {microlearningTab === "journal" && (
                        <div>
                          {journalEntries.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">No journal entries found</p>
                          ) : (
                            <div className="space-y-4">
                              {journalEntries.map((entry) => (
                                <div
                                  key={entry.id}
                                  className="border border-gray-200 rounded-lg p-4"
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded">
                                      {entry.measureName}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {formatDate(entry.createdOn)}
                                    </span>
                                  </div>
                                  <p className="text-gray-700 font-medium mb-2">{entry.promptText}</p>
                                  <div className="bg-gray-50 p-3 rounded text-gray-600">
                                    {entry.response}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Scenario Completions Tab */}
                      {microlearningTab === "scenarios" && (
                        <div>
                          {scenarioCompletions.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">No scenario completions found</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                                    <th className="text-left p-3 font-medium text-gray-600">Measure</th>
                                    <th className="text-left p-3 font-medium text-gray-600">Selected Choice</th>
                                    <th className="text-left p-3 font-medium text-gray-600">Result</th>
                                    <th className="text-left p-3 font-medium text-gray-600">Score</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {scenarioCompletions.map((completion) => (
                                    <tr key={completion.id} className="hover:bg-gray-50">
                                      <td className="p-3 text-gray-500">
                                        {formatDateShort(completion.completedDate)}
                                      </td>
                                      <td className="p-3">
                                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                          {completion.measureName}
                                        </span>
                                      </td>
                                      <td className="p-3 text-gray-700 max-w-xs truncate">
                                        {completion.selectedChoice}
                                      </td>
                                      <td className="p-3">
                                        {completion.isCorrect ? (
                                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                            Correct
                                          </span>
                                        ) : (
                                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                            Incorrect
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-3 font-medium text-gray-900">
                                        {completion.score}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Weekly Responses Tab */}
                      {microlearningTab === "weekly" && (
                        <div>
                          {weeklyResponses.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">No weekly responses found</p>
                          ) : (
                            <div className="space-y-4">
                              {weeklyResponses.map((response) => (
                                <div
                                  key={response.id}
                                  className="border border-gray-200 rounded-lg p-4"
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded">
                                        {response.measureName}
                                      </span>
                                      <span className="text-sm text-gray-500">
                                        Week {response.weekNumber}, Month {response.monthNumber}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {response.score !== null && (
                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
                                          Score: {response.score}
                                        </span>
                                      )}
                                      <span className="text-sm text-gray-500">
                                        {formatDate(response.createdOn)}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-gray-700 font-medium mb-2">{response.prompt}</p>
                                  <div className="bg-gray-50 p-3 rounded text-gray-600">
                                    {response.response}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Empty State - no user selected */}
            {!selectedUser && debouncedQuery.length < 2 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <DatabaseIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Search for a User</h3>
                <p className="text-gray-500">
                  Enter at least 2 characters to search for a user and view their raw data.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default RawDataPage
